/**
 * ASCII DXF okuyucu (bağımlılıksız) — kesit içe aktarma için gereken kadarı:
 * LINE, LWPOLYLINE (bulge dâhil), POLYLINE/VERTEX (2B), ARC, CIRCLE ve INSERT (blok yerleştirme: öteleme, ölçek, dönme; iç içe bloklar).
 * Her varlık düz doğru parçalarına ("segment") indirgenir; yaylar ≤ 7,5° adımla örneklenir. Kapalı polyline'lar ayrıca `closed` ile işaretlenir.
 */
export interface Pt2 { x: number; y: number }
export interface Segment { a: Pt2; b: Pt2; layer: string; closedLoop?: number }   // closedLoop: aynı kapalı polyline'ın kimliği
export interface DxfDrawing { segments: Segment[]; loops: Pt2[][]; layers: string[]; insunits: number | null; entityCount: number }

interface Pair { code: number; value: string }
const ARC_STEP = (7.5 * Math.PI) / 180;

/** DXF metnini (grup kodu, değer) çiftlerine ayırır. İkili (binary) DXF reddedilir. */
export function tokenize(text: string): Pair[] {
  if (text.startsWith("AutoCAD Binary DXF")) throw new Error("İkili (binary) DXF desteklenmiyor — dosyayı ASCII DXF olarak kaydedin.");
  const lines = text.split(/\r\n|\n|\r/);
  const out: Pair[] = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = Number(lines[i]!.trim());
    if (!Number.isFinite(code)) throw new Error(`DXF okunamadı: ${i + 1}. satırda grup kodu bekleniyordu.`);
    out.push({ code, value: lines[i + 1]!.trim() });
  }
  if (!out.some((p) => p.code === 0 && p.value === "SECTION")) throw new Error("DXF okunamadı: SECTION bulunamadı (dosya DXF değil?).");
  return out;
}

type Xf = { a: number; b: number; c: number; d: number; e: number; f: number };   // [a c e; b d f] afin
const IDENT: Xf = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
const apply = (m: Xf, p: Pt2): Pt2 => ({ x: m.a * p.x + m.c * p.y + m.e, y: m.b * p.x + m.d * p.y + m.f });
const mul = (m: Xf, n: Xf): Xf => ({ a: m.a * n.a + m.c * n.b, b: m.b * n.a + m.d * n.b, c: m.a * n.c + m.c * n.d, d: m.b * n.c + m.d * n.d, e: m.a * n.e + m.c * n.f + m.e, f: m.b * n.e + m.d * n.f + m.f });

interface Entity { type: string; pairs: Pair[]; children?: Entity[] }   // POLYLINE → VERTEX children

/** Varlık listesini (ENTITIES ya da bir BLOCK gövdesi) ayrıştırır. */
function readEntities(pairs: Pair[], start: number, end: number): Entity[] {
  const out: Entity[] = [];
  let cur: Entity | null = null, poly: Entity | null = null;
  for (let i = start; i < end; i++) {
    const p = pairs[i]!;
    if (p.code === 0) {
      if (p.value === "VERTEX" && poly) { cur = { type: "VERTEX", pairs: [] }; poly.children!.push(cur); continue; }
      if (p.value === "SEQEND") { poly = null; cur = null; continue; }
      cur = { type: p.value, pairs: [] };
      if (p.value === "POLYLINE") { cur.children = []; poly = cur; }
      out.push(cur);
    } else if (cur) cur.pairs.push(p);
  }
  return out;
}
const val = (e: Entity, code: number, dflt = 0): number => { const p = e.pairs.find((q) => q.code === code); return p ? Number(p.value) : dflt; };
const str = (e: Entity, code: number, dflt = ""): string => e.pairs.find((q) => q.code === code)?.value ?? dflt;

function arcPoints(c: Pt2, r: number, a0: number, a1: number): Pt2[] {
  let sweep = a1 - a0; while (sweep <= 0) sweep += 2 * Math.PI;
  const n = Math.max(1, Math.ceil(sweep / ARC_STEP));
  const pts: Pt2[] = [];
  for (let k = 0; k <= n; k++) { const a = a0 + (sweep * k) / n; pts.push({ x: c.x + r * Math.cos(a), y: c.y + r * Math.sin(a) }); }
  return pts;
}
/** Bulge'lu kenar (a→b, bulge = tan(θ/4)) → ara noktalar dâhil nokta dizisi (a hariç, b dâhil). */
function bulgeArc(a: Pt2, b: Pt2, bulge: number): Pt2[] {
  if (Math.abs(bulge) < 1e-9) return [b];
  const th = 4 * Math.atan(bulge);
  const chord = Math.hypot(b.x - a.x, b.y - a.y);
  const r = chord / (2 * Math.sin(Math.abs(th) / 2));
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
  const d = Math.sqrt(Math.max(0, r * r - (chord / 2) ** 2)) * Math.sign(bulge);
  const nx = -(b.y - a.y) / chord, ny = (b.x - a.x) / chord;                       // sol normal
  const c = { x: mx - nx * d, y: my - ny * d };
  const a0 = Math.atan2(a.y - c.y, a.x - c.x);
  const n = Math.max(1, Math.ceil(Math.abs(th) / ARC_STEP));
  const pts: Pt2[] = [];
  for (let k = 1; k <= n; k++) { const t = a0 + (th * k) / n; pts.push({ x: c.x + r * Math.cos(t), y: c.y + r * Math.sin(t) }); }
  pts[pts.length - 1] = b;
  return pts;
}

export function parseDxf(text: string): DxfDrawing {
  const pairs = tokenize(text);
  // Bölümler
  const sections: { name: string; start: number; end: number }[] = [];
  for (let i = 0; i < pairs.length; i++) {
    if (pairs[i]!.code === 0 && pairs[i]!.value === "SECTION" && pairs[i + 1]?.code === 2) {
      let j = i + 2; while (j < pairs.length && !(pairs[j]!.code === 0 && pairs[j]!.value === "ENDSEC")) j++;
      sections.push({ name: pairs[i + 1]!.value, start: i + 2, end: j }); i = j;
    }
  }
  const sec = (n: string) => sections.find((s) => s.name === n);
  let insunits: number | null = null;
  const hdr = sec("HEADER");
  if (hdr) for (let i = hdr.start; i < hdr.end; i++) if (pairs[i]!.code === 9 && pairs[i]!.value === "$INSUNITS" && pairs[i + 1]?.code === 70) insunits = Number(pairs[i + 1]!.value);
  // Bloklar: ad → (taban noktası, varlıklar)
  const blocks = new Map<string, { base: Pt2; ents: Entity[] }>();
  const blk = sec("BLOCKS");
  if (blk) {
    for (let i = blk.start; i < blk.end; i++) {
      if (pairs[i]!.code === 0 && pairs[i]!.value === "BLOCK") {
        let j = i + 1; while (j < blk.end && !(pairs[j]!.code === 0 && pairs[j]!.value === "ENDBLK")) j++;
        const head = readEntities(pairs, i, j);                                  // ilk öğe BLOCK başlığı, kalanı varlıklar
        const b = head[0]!;
        blocks.set(str(b, 2), { base: { x: val(b, 10), y: val(b, 20) }, ents: head.slice(1) });
        i = j;
      }
    }
  }
  const ent = sec("ENTITIES");
  if (!ent) throw new Error("DXF okunamadı: ENTITIES bölümü yok.");
  const segments: Segment[] = [];
  const loops: Pt2[][] = [];
  const layers = new Set<string>();
  let entityCount = 0, loopId = 0;

  const emitPolyline = (pts: Pt2[], bulges: number[], closed: boolean, layer: string, m: Xf): void => {
    if (pts.length < 2) return;
    const chain: Pt2[] = [apply(m, pts[0]!)];
    const n = closed ? pts.length : pts.length - 1;
    for (let k = 0; k < n; k++) {
      const a = pts[k]!, b = pts[(k + 1) % pts.length]!;
      for (const q of bulgeArc(a, b, bulges[k] ?? 0)) chain.push(apply(m, q));
    }
    const id = closed ? ++loopId : undefined;
    for (let k = 0; k + 1 < chain.length; k++) segments.push({ a: chain[k]!, b: chain[k + 1]!, layer, ...(id ? { closedLoop: id } : {}) });
    if (closed) { const ring = chain.slice(0, -1); if (ring.length >= 3) loops.push(ring); }
  };
  const walk = (ents: Entity[], m: Xf, depth: number): void => {
    for (const e of ents) {
      const layer = str(e, 8, "0"); layers.add(layer);
      if (e.type === "LINE") { entityCount++; segments.push({ a: apply(m, { x: val(e, 10), y: val(e, 20) }), b: apply(m, { x: val(e, 11), y: val(e, 21) }), layer }); }
      else if (e.type === "LWPOLYLINE") {
        entityCount++;
        const pts: Pt2[] = []; const bulges: number[] = [];
        for (const p of e.pairs) { if (p.code === 10) { pts.push({ x: Number(p.value), y: 0 }); bulges.push(0); } else if (p.code === 20 && pts.length) pts[pts.length - 1]!.y = Number(p.value); else if (p.code === 42 && pts.length) bulges[pts.length - 1] = Number(p.value); }
        emitPolyline(pts, bulges, (val(e, 70) & 1) === 1, layer, m);
      } else if (e.type === "POLYLINE") {
        entityCount++;
        const vs = (e.children ?? []).filter((v) => (val(v, 70) & 16) === 0);   // spline kontrol noktalarını atla
        emitPolyline(vs.map((v) => ({ x: val(v, 10), y: val(v, 20) })), vs.map((v) => val(v, 42)), (val(e, 70) & 1) === 1, layer, m);
      } else if (e.type === "ARC") {
        entityCount++;
        const pts = arcPoints({ x: val(e, 10), y: val(e, 20) }, val(e, 40), (val(e, 50) * Math.PI) / 180, (val(e, 51) * Math.PI) / 180).map((p) => apply(m, p));
        for (let k = 0; k + 1 < pts.length; k++) segments.push({ a: pts[k]!, b: pts[k + 1]!, layer });
      } else if (e.type === "CIRCLE") {
        entityCount++;
        const pts = arcPoints({ x: val(e, 10), y: val(e, 20) }, val(e, 40), 0, 2 * Math.PI - 1e-9).map((p) => apply(m, p));
        emitPolyline(pts.slice(0, -1), [], true, layer, IDENT);
      } else if (e.type === "INSERT" && depth < 8) {
        const b = blocks.get(str(e, 2)); if (!b) continue;
        const sx = val(e, 41, 1), sy = val(e, 42, 1), rot = (val(e, 50) * Math.PI) / 180;
        const cos = Math.cos(rot), sin = Math.sin(rot);
        const local: Xf = { a: sx * cos, b: sx * sin, c: -sy * sin, d: sy * cos, e: val(e, 10), f: val(e, 20) };
        const shift: Xf = { ...IDENT, e: -b.base.x, f: -b.base.y };
        walk(b.ents, mul(m, mul(local, shift)), depth + 1);
      }
    }
  };
  walk(readEntities(pairs, ent.start, ent.end), IDENT, 0);
  return { segments, loops, layers: [...layers].sort(), insunits, entityCount };
}

/** $INSUNITS → metreye çarpan (bilinmiyorsa null). 1 inç, 2 ft, 4 mm, 5 cm, 6 m */
export function unitsToMetre(insunits: number | null): number | null {
  return insunits === 1 ? 0.0254 : insunits === 2 ? 0.3048 : insunits === 4 ? 0.001 : insunits === 5 ? 0.01 : insunits === 6 ? 1 : null;
}
/** Çizim büyüklüğünden birim tahmini (duvar kesiti 1–15 m): > 400 → mm, > 40 → cm, yoksa m. */
export function guessUnitFactor(extent: number): number { return extent > 400 ? 0.001 : extent > 40 ? 0.01 : 1; }
